/**
 * 菜单来源：数据库。
 *
 * ## 拉不到就退回路由表
 *
 * 菜单是这个应用的骨架 —— 接口一抖，整个侧栏空掉，用户看到的是一个
 * 「什么都没有」的平台，而不是「菜单加载失败」。所以保留降级：
 * `failed` 为 true 时 `permission.ts` 回到由 `routes.ts` 推导的那套。
 *
 * 降级必须是**有声的**：`failed` 要被界面读出来。悄悄降级的后果是管理员在
 * 菜单管理里改了半天，刷新后看到的还是旧菜单，却以为是自己没保存成功。
 *
 * ## 单飞
 *
 * 路由守卫每次导航都会调 `ensureLoaded()`，并发导航会同时进来。
 * 不单飞就会对 `/menus/me` 打出一串重复请求，`addRoute` 也会被重复执行。
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { Router } from 'vue-router'

import { fetchMyMenus, type MenuNode as ApiMenuNode } from '@/api/menus'
import { registerMenuRoutes } from '@/router/dynamic'
import type { MenuNode as UiMenuNode } from '@/stores/permission'

/** 侧栏要不要画这一项。`show_status === '0'` = 藏起来（路由照常注册）。 */
function shown(n: { show_status?: string }): boolean {
  return n.show_status !== '0'
}

export const useMenuStore = defineStore('menu', () => {
  const tree = ref<ApiMenuNode[]>([])
  const loaded = ref(false)
  /** 拉取失败过。为 true 时上层回退到路由表推导的菜单。 */
  const failed = ref(false)
  let inflight: Promise<boolean> | null = null

  /**
   * 拉菜单并注册路由。
   *
   * 返回**是否新注册了路由** —— 调用方（路由守卫）要据此重试当前导航，
   * 否则刷新一个动态页面会 404，而再点一次就正常。这种毛病最难查。
   */
  async function ensureLoaded(router: Router): Promise<boolean> {
    if (loaded.value || failed.value) return false
    if (inflight) return inflight

    inflight = (async () => {
      try {
        const page = await fetchMyMenus()
        tree.value = page.items
        const { added, missing } = registerMenuRoutes(router, page.items)
        for (const m of missing) {
          if (m.showStatus === '0') {
            // 已隐藏的坏菜单不在侧栏里，用户点不到 —— 降为 warn 留痕即可，
            // error 会把「配置债」和「用户此刻就会撞上的空白页」混在一起。
            console.warn(
              `[menu]「${m.menuName}」已隐藏，但组件路径在 views/ 下仍不存在：${m.component}。` +
                '页面做出来之后在菜单管理里改对路径再打开显示。',
            )
            continue
          }
          // 不静默跳过：这条菜单会照常出现在侧栏，点进去却是一片空白。
          console.error(
            `[menu]「${m.menuName}」的组件路径在 views/ 下不存在：${m.component}。` +
              '该菜单点得开，但会是空白页。请在菜单管理里修正它。',
          )
        }
        loaded.value = true
        return added > 0
      } catch {
        failed.value = true
        return false
      } finally {
        inflight = null
      }
    })()

    return inflight
  }

  function reset(): void {
    tree.value = []
    loaded.value = false
    failed.value = false
    inflight = null
  }

  /**
   * 转成侧栏认识的形状。目录 → `group`，页面 → `leaf`。
   *
   * `show_status === '0'` 的在这里过滤，且只在画侧栏这一步：`router/dynamic.ts`
   * 注册路由走的是未过滤的 `tree`，滤掉那里会让隐藏页面的直链 404，而「进不去」
   * 该由权限决定。后端如实下发这一位（`schemas/menu.py::MenuNode.show_status`）。
   * 子项全隐时目录也不画：一个点开来是空的分组比没有这个分组更让人困惑。
   * 仅超管可见的行（`superadmin_only`）由后端裁掉，这里不再判。
   */
  const uiTree = computed<UiMenuNode[]>(() =>
    tree.value
      .filter(shown)
      .map((n) =>
        n.menu_type === '1'
          ? {
              kind: 'group' as const,
              title: n.menu_name,
              ...(n.icon_code ? { icon: n.icon_code } : {}),
              children: (n.children ?? []).filter(shown).map((c) => ({
                kind: 'leaf' as const,
                name: c.route_name ?? '',
                title: c.menu_name,
                ...(c.icon_code ? { icon: c.icon_code } : {}),
                ...(c.perm_code ? { perms: [c.perm_code] } : {}),
              })),
            }
          : {
              kind: 'leaf' as const,
              name: n.route_name ?? '',
              title: n.menu_name,
              ...(n.icon_code ? { icon: n.icon_code } : {}),
              ...(n.perm_code ? { perms: [n.perm_code] } : {}),
            },
      )
      .filter((n) => n.kind !== 'group' || n.children.length > 0),
  )

  /**
   * 下发树里全部页面的 `route_name`（含 `show_status='0'` 的隐藏项 ——
   * 隐藏只影响画不画侧栏，直链照进；没在这张集合里才是「被裁掉了」：
   * 角色管理页没勾，或者这一行 `superadmin_only` 而登录者不是平台超管）。
   *
   * 守卫用它拦「菜单被裁但路由是静态注册」的页面：`/menus/me` 的筛选只影响下发，
   * 而 `routes.ts` 静态注册的路由永远都在，不拦的话直链照样能进。
   */
  const allowedRouteNames = computed<Set<string>>(() => {
    const out = new Set<string>()
    for (const node of tree.value) {
      if (node.route_name) out.add(node.route_name)
      for (const child of node.children ?? []) {
        if (child.route_name) out.add(child.route_name)
      }
    }
    return out
  })

  /** 侧栏第一个可见页面的 `route_name`；一个都没有时为 null。登录落点用。 */
  const firstRouteName = computed<string | null>(() => {
    for (const group of uiTree.value) {
      if (group.kind === 'leaf' && group.name) return group.name
      if (group.kind === 'group') {
        const leaf = group.children.find((c) => c.name)
        if (leaf) return leaf.name
      }
    }
    return null
  })

  return { tree, loaded, failed, uiTree, allowedRouteNames, firstRouteName, ensureLoaded, reset }
})
